/** Static internal-team SUV for the Adventure garage. This is decorative:
 * it never creates a collider, agent, cue, route or simulation event.
 * Source/provenance: public/models/neighborhood/benchmark/parked-car.manifest.json.
 */
import type { Scene } from '@babylonjs/core/scene';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { themeAssetPath } from './themes';
import type { AdventureActor } from './adventureCast';
import '@babylonjs/loaders/glTF';

type LoadState = { pending: Set<string>; failed: Set<string> };
export interface AdventureParkedCarOptions {
  loadState?: LoadState;
  onReady?: (meshes: Mesh[]) => void;
  roadHalfWidth?: number;
  centerX?: number;
  centerZ?: number;
  groundY?: number;
  maxWidth?: number;
  maxLength?: number;
  /** Runtime paint only; the copied source GLB remains byte-for-byte intact. */
  paintColor?: string;
}

function worldBounds(meshes: readonly Mesh[]): { min: Vector3; max: Vector3 } {
  let min = new Vector3(Infinity, Infinity, Infinity);
  let max = new Vector3(-Infinity, -Infinity, -Infinity);
  for (const mesh of meshes) {
    mesh.computeWorldMatrix(true);
    const box = mesh.getBoundingInfo().boundingBox;
    min = Vector3.Minimize(min, box.minimumWorld);
    max = Vector3.Maximize(max, box.maximumWorld);
  }
  return { min, max };
}

/** Returns an empty garage anchor immediately. Only this instance owns its
 * downloaded container; disposal and stale requests cannot affect its successor. */
export function buildAdventureParkedCar(scene: Scene, options: AdventureParkedCarOptions = {}): AdventureActor {
  const root = new TransformNode('adventureParkedCar', scene);
  const meshes: Mesh[] = [];
  const roadHalfWidth = options.roadHalfWidth ?? 2.4;
  const centerX = options.centerX ?? -(roadHalfWidth + .95);
  root.position.set(centerX, options.groundY ?? .16, options.centerZ ?? 20.1);
  const path = themeAssetPath('neighborhood', 'models', 'benchmark/parked-car');
  if (!path) { root.dispose(); throw new Error('Missing Adventure benchmark parked-car asset'); }
  const url = `${import.meta.env.BASE_URL}${path}`;
  let disposed = false;
  let container: AssetContainer | undefined;
  options.loadState?.pending.add(url);
  void LoadAssetContainerAsync(url, scene).then(loaded => {
    if (disposed || scene.isDisposed || root.isDisposed()) { loaded.dispose(); return; }
    container = loaded;
    // The fit hierarchy stays at world origin until orientation and bounds are
    // measured. That avoids mixing the garage position into source dimensions.
    const fit = new TransformNode('adventureParkedCar_fit', scene);
    loaded.addAllToScene();
    for (const node of loaded.rootNodes) if (node instanceof TransformNode) node.parent = fit;
    for (const mesh of loaded.meshes) {
      mesh.isPickable = false;
      mesh.receiveShadows = true;
      if (mesh instanceof Mesh && mesh.getTotalVertices() > 0) meshes.push(mesh);
    }
    if (!meshes.length) { fit.dispose(); throw new Error('Parked SUV contains no visible geometry'); }
    const source = worldBounds(meshes);
    const centre = source.min.add(source.max).scale(.5);
    const headlight = meshes.find(mesh => /HeadLight/i.test(mesh.name));
    if (!headlight) { fit.dispose(); throw new Error('Parked SUV front marker is missing'); }
    // glTF's handedness conversion is owned by its loader root. Inspect the
    // actual headlamp instead of assuming that source +Z is engine +Z.
    const frontZ = headlight.getBoundingInfo().boundingBox.centerWorld.z - centre.z;
    fit.rotation.y = frontZ > 0 ? Math.PI : 0;
    const oriented = worldBounds(meshes);
    const size = oriented.max.subtract(oriented.min);
    // Include mirrors in the fit and reserve at least 4cm beyond the road edge.
    // A caller cannot accidentally protrude this decorative SUV into driving space.
    const offRoadWidth = 2 * Math.max(0, Math.abs(centerX) - roadHalfWidth - .04);
    const maxWidth = Math.min(options.maxWidth ?? 1.8, offRoadWidth);
    const maxLength = options.maxLength ?? 4.5;
    if (maxWidth <= .1 || maxLength <= .1 || size.x <= 0 || size.z <= 0) {
      fit.dispose(); throw new Error('Parked SUV does not fit outside the carriageway');
    }
    const scale = Math.min(maxWidth / size.x, maxLength / size.z);
    fit.scaling.setAll(scale);
    fit.position.set(-(oriented.min.x + oriented.max.x) * .5 * scale,
      -oriented.min.y * scale, -(oriented.min.z + oriented.max.z) * .5 * scale);
    fit.parent = root;
    for (const material of loaded.materials) {
      if (!(material instanceof PBRMaterial)) continue;
      material.environmentIntensity = .9;
      material.emissiveColor.setAll(0); // It is parked, with the lamps switched off.
      if (material.name === 'Body') {
        material.albedoColor = Color3.FromHexString(options.paintColor ?? '#A6A496').toLinearSpace();
        material.metallic = .52; material.roughness = .32;
        material.clearCoat.isEnabled = true;
        material.clearCoat.intensity = .65; material.clearCoat.roughness = .22;
      } else if (/^Glass/.test(material.name)) {
        // The source has no interior. Preserve its opaque tinted glazing.
        material.metallic = .12; material.roughness = .13;
        material.clearCoat.isEnabled = true;
        material.clearCoat.intensity = .5; material.clearCoat.roughness = .1;
      } else if (/^MetalFrame/.test(material.name)) {
        material.metallic = .85; material.roughness = .28;
      } else if (/^BlackPlastic/.test(material.name)) {
        material.metallic = 0; material.roughness = .88;
      } else if (/^ShinnyPlastic/.test(material.name)) {
        material.metallic = 0; material.roughness = .34;
      } else if (/Light/.test(material.name)) {
        material.metallic = .05; material.roughness = .22;
      }
    }
    for (const mesh of meshes) mesh.name = `adventureParkedCar_${mesh.name}`;
    for (const animation of loaded.animationGroups) animation.stop();
    options.loadState?.pending.delete(url);
    options.loadState?.failed.delete(url);
    options.onReady?.(meshes);
  }).catch(error => {
    if (disposed) return;
    container?.dispose(); container = undefined;
    meshes.length = 0;
    options.loadState?.pending.delete(url);
    options.loadState?.failed.add(url);
    console.error('Adventure parked SUV failed to load', error);
  });
  return {
    root, meshes,
    update() { /* Parked scenery has no simulation-driven behavior. */ },
    dispose() {
      if (disposed) return;
      disposed = true;
      options.loadState?.pending.delete(url);
      container?.dispose(); container = undefined;
      root.dispose();
      meshes.length = 0;
    },
  };
}
